import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, Loader2, ArrowRight } from "lucide-react";
import { KineticLine, Reveal, Overline } from "@/components/Kinetic";
import { CONTACT, PRODUCTS, COUNTRY_CODES } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const initialForm = {
  name: "", company: "", email: "", phone: "", country: "", product_interest: "", message: "",
};

const inputCls =
  "w-full border border-forest-border bg-forest-surface px-4 py-3.5 text-sm font-light text-cream placeholder:text-sage/50 outline-none transition-colors duration-300 focus:border-gold";

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [code, setCode] = useState("+91");
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const payload = {
        ...form,
        phone: form.phone && !form.phone.trim().startsWith("+") ? `${code} ${form.phone.trim()}` : form.phone,
      };
      await axios.post(`${API}/enquiries`, payload);
      toast.success("Enquiry received. Our team will reach out to you shortly.");
      setForm(initialForm);
    } catch (err) {
      toast.error("Could not send your enquiry. Please try again or email us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="contact-page">
      {/* HERO */}
      <section className="mx-auto max-w-7xl px-6 pb-16 pt-24 lg:px-10 lg:pt-32" data-testid="contact-hero">
        <Overline>Contact Nishmayy Global</Overline>
        <h1 className="mt-6 font-serif font-light leading-[0.95] tracking-tight text-cream" data-testid="contact-heading">
          <KineticLine delay={0.25} className="text-5xl md:text-6xl lg:text-7xl">
            Global Partnerships
          </KineticLine>
          <KineticLine delay={0.4} className="text-5xl md:text-6xl lg:text-7xl">
            Begin Here<span className="text-gold">.</span>
          </KineticLine>
        </h1>
        <Reveal delay={0.7} className="mt-8 max-w-2xl">
          <p className="text-base font-light leading-relaxed text-sage md:text-lg">
            Tell us what you're sourcing — dry fruits, grains, spices or a full commodity program.
            We typically respond within one business day.
          </p>
        </Reveal>
      </section>

      {/* CONTENT */}
      <section className="mx-auto max-w-7xl px-6 pb-32 lg:px-10">
        <div className="grid gap-16 lg:grid-cols-12">
          {/* DETAILS */}
          <div className="lg:col-span-5">
            <Reveal>
              <div className="space-y-px border border-forest-border bg-forest-border">
                <a href={`mailto:${CONTACT.email}`} data-testid="contact-email" className="group flex items-center gap-5 bg-forest-surface px-7 py-6 transition-colors duration-300 hover:bg-forest-light">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center border border-gold/40 text-gold">
                    <Mail className="h-4 w-4" strokeWidth={1.5} />
                  </span>
                  <span>
                    <span className="block text-[10px] uppercase tracking-[0.25em] text-sage">Email</span>
                    <span className="mt-1 block font-serif text-xl text-cream transition-colors group-hover:text-gold">{CONTACT.email}</span>
                  </span>
                </a>
                <div className="flex items-center gap-5 bg-forest-surface px-7 py-6" data-testid="contact-address">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center border border-gold/40 text-gold">
                    <MapPin className="h-4 w-4" strokeWidth={1.5} />
                  </span>
                  <span>
                    <span className="block text-[10px] uppercase tracking-[0.25em] text-sage">Processing Unit & Office</span>
                    <span className="mt-1 block font-serif text-xl text-cream">{CONTACT.address}</span>
                  </span>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-8 border-l-2 border-gold pl-5 font-serif text-xl font-light italic leading-relaxed text-sage">
                "Quality you can trust. Supply you can rely on. Partnerships that last."
              </p>
            </Reveal>
          </div>

          {/* FORM */}
          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <form onSubmit={submit} className="border border-forest-border bg-forest-surface p-8 lg:p-12" data-testid="enquiry-form">
                <Overline>Send an Enquiry</Overline>
                <div className="mt-8 grid gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="enq-name" className="mb-2 block text-xs uppercase tracking-[0.2em] text-sage">Full Name *</label>
                    <input id="enq-name" data-testid="enquiry-name" required minLength={2} value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="enq-company" className="mb-2 block text-xs uppercase tracking-[0.2em] text-sage">Company</label>
                    <input id="enq-company" data-testid="enquiry-company" value={form.company} onChange={set("company")} placeholder="Company / organization" className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="enq-email" className="mb-2 block text-xs uppercase tracking-[0.2em] text-sage">Email *</label>
                    <input id="enq-email" data-testid="enquiry-email" type="email" required value={form.email} onChange={set("email")} placeholder="you@company.com" className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="enq-phone" className="mb-2 block text-xs uppercase tracking-[0.2em] text-sage">Phone</label>
                    <div className="flex">
                      <select
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        data-testid="enquiry-phone-code"
                        aria-label="Country code"
                        className="w-36 shrink-0 cursor-pointer border border-r-0 border-forest-border bg-forest px-3 text-sm text-sage outline-none transition-colors duration-300 focus:border-gold"
                      >
                        {COUNTRY_CODES.map((c) => (
                          <option key={c.code} value={c.code}>{c.code} · {c.label}</option>
                        ))}
                      </select>
                      <input id="enq-phone" data-testid="enquiry-phone" value={form.phone} onChange={set("phone")} placeholder="98XXX XXXXX" className={inputCls} />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="enq-country" className="mb-2 block text-xs uppercase tracking-[0.2em] text-sage">Country</label>
                    <input id="enq-country" data-testid="enquiry-country" value={form.country} onChange={set("country")} placeholder="Destination country" className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="enq-product" className="mb-2 block text-xs uppercase tracking-[0.2em] text-sage">Product Interest</label>
                    <select id="enq-product" data-testid="enquiry-product" value={form.product_interest} onChange={set("product_interest")} className={inputCls}>
                      <option value="">Select a category</option>
                      {PRODUCTS.map((p) => (
                        <option key={p.slug} value={p.name}>{p.name}</option>
                      ))}
                      <option value="Other">Other / Multiple</option>
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="enq-message" className="mb-2 block text-xs uppercase tracking-[0.2em] text-sage">Message *</label>
                    <textarea id="enq-message" data-testid="enquiry-message" required minLength={5} rows={5} value={form.message} onChange={set("message")} placeholder="Quantities, specifications, destination port, timelines..." className={`${inputCls} resize-y`} />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="enquiry-submit-button"
                  className="group mt-8 flex items-center gap-3 bg-gold px-10 py-4 text-sm font-semibold tracking-wide text-forest transition-colors duration-300 hover:bg-cream disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {sending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                    </>
                  ) : (
                    <>
                      Submit Enquiry
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </>
                  )}
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
}
